/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { WebConfiguration as __Configuration } from '../web-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { CarrierList } from '../models/carrier-list';
import { CarrierSearchOptions } from '../models/carrier-search-options';
import { Carrier } from '../models/carrier';
import { CarrierTerminal } from '../models/carrier-terminal';
import { User } from '../models/user';
import { Vehicle } from '../models/vehicle';
import { VehicleCreate } from '../models/vehicle-create';
import { VehicleUpdate } from '../models/vehicle-update';
import { MainCarrierContract } from '../models/main-carrier-contract';
import { CarrierContractCreate } from '../models/carrier-contract-create';
import { CarrierContract } from '../models/carrier-contract';
import { CarrierContractUpdate } from '../models/carrier-contract-update';

/**
 * All about the carriers
 */
@Injectable({
  providedIn: 'root',
})
class CarriersService extends __BaseService {
  static readonly searchCarriersPath = '/carriers/search';
  static readonly getCarrierPath = '/carriers/{uid}';
  static readonly getCarrierTerminalsPath = '/carriers/{uid}/terminals';
  static readonly linkTerminalToCarrierPath = '/carriers/{uid}/terminals/{terminalUid}';
  static readonly unlinkCarrierTerminalPath = '/carriers/{uid}/terminals/{terminalUid}';
  static readonly SetCarrierTerminalAsDefaultPath = '/carriers/{uid}/terminals/{terminalUid}/default';
  static readonly getCarrierDriversPath = '/carriers/{uid}/drivers';
  static readonly linkDriverToCarrierPath = '/carriers/{uid}/drivers/{userUid}';
  static readonly unlinkCarrierDriverPath = '/carriers/{uid}/drivers/{userUid}';
  static readonly getCarrierOperatorsPath = '/carriers/{uid}/operators';
  static readonly linkOperatorToCarrierPath = '/carriers/{uid}/operators/{userUid}';
  static readonly unlinkCarrierOperatorPath = '/carriers/{uid}/operators/{userUid}';
  static readonly getCarrierVehiclesPath = '/carriers/{uid}/vehicles';
  static readonly createCarrierVehiclePath = '/carriers/{uid}/vehicles';
  static readonly getCarrierVehiclePath = '/carriers/{uid}/vehicles/{vehicleUid}';
  static readonly updateCarrierVehiclePath = '/carriers/{uid}/vehicles/{vehicleUid}';
  static readonly deleteCarrierVehiclePath = '/carriers/{uid}/vehicles/{vehicleUid}';
  static readonly getCarrierContractsPath = '/carriers/{uid}/contracts';
  static readonly createCarrierContractPath = '/carriers/{uid}/contracts';
  static readonly retrieveCarrierContractPath = '/carriers/{uid}/contracts/{carrierContractUid}';
  static readonly updateCarrierContractPath = '/carriers/{uid}/contracts/{carrierContractUid}';
  static readonly deleteCarrierContractPath = '/carriers/{uid}/contracts/{carrierContractUid}';

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Search carriers, with pagination
   *
   * Search carriers, with pagination
   * @param params The `CarriersService.SearchCarriersParams` containing the following parameters:
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * NAME : the carrier's name
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The carrier search options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success
   */
  searchCarriersResponse(params: CarriersService.SearchCarriersParams): __Observable<__StrictHttpResponse<CarrierList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.sortFieldName != null) __params = __params.set('sortFieldName', params.sortFieldName.toString());
    if (params.sortDirection != null) __params = __params.set('sortDirection', params.sortDirection.toString());
    __body = params.search;
    if (params.offset != null) __params = __params.set('offset', params.offset.toString());
    if (params.limit != null) __params = __params.set('limit', params.limit.toString());
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/carriers/search`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<CarrierList>;
      })
    );
  }
  /**
   * Search carriers, with pagination
   *
   * Search carriers, with pagination
   * @param params The `CarriersService.SearchCarriersParams` containing the following parameters:
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * NAME : the carrier's name
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The carrier search options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success
   */
  searchCarriers(params: CarriersService.SearchCarriersParams): __Observable<CarrierList> {
    return this.searchCarriersResponse(params).pipe(
      __map(_r => _r.body as CarrierList)
    );
  }

  /**
   * Get a carrier
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierResponse(uid: string): __Observable<__StrictHttpResponse<Carrier>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/carriers/${encodeURIComponent(String(uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Carrier>;
      })
    );
  }
  /**
   * Get a carrier
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrier(uid: string): __Observable<Carrier> {
    return this.getCarrierResponse(uid).pipe(
      __map(_r => _r.body as Carrier)
    );
  }

  /**
   * Get a carrier's terminals
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierTerminalsResponse(uid: string): __Observable<__StrictHttpResponse<Array<CarrierTerminal>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/carriers/${encodeURIComponent(String(uid))}/terminals`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<CarrierTerminal>>;
      })
    );
  }
  /**
   * Get a carrier's terminals
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierTerminals(uid: string): __Observable<Array<CarrierTerminal>> {
    return this.getCarrierTerminalsResponse(uid).pipe(
      __map(_r => _r.body as Array<CarrierTerminal>)
    );
  }

  /**
   * Link a terminal to a carrier
   * @param params The `CarriersService.LinkTerminalToCarrierParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `terminalUid`: The object ID. Must be a valid ID.
   */
  linkTerminalToCarrierResponse(params: CarriersService.LinkTerminalToCarrierParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/terminals/${encodeURIComponent(String(params.terminalUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Link a terminal to a carrier
   * @param params The `CarriersService.LinkTerminalToCarrierParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `terminalUid`: The object ID. Must be a valid ID.
   */
  linkTerminalToCarrier(params: CarriersService.LinkTerminalToCarrierParams): __Observable<null> {
    return this.linkTerminalToCarrierResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Unlink a carrier's terminal
   * @param params The `CarriersService.UnlinkCarrierTerminalParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `terminalUid`: The object ID. Must be a valid ID.
   */
  unlinkCarrierTerminalResponse(params: CarriersService.UnlinkCarrierTerminalParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/terminals/${encodeURIComponent(String(params.terminalUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Unlink a carrier's terminal
   * @param params The `CarriersService.UnlinkCarrierTerminalParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `terminalUid`: The object ID. Must be a valid ID.
   */
  unlinkCarrierTerminal(params: CarriersService.UnlinkCarrierTerminalParams): __Observable<null> {
    return this.unlinkCarrierTerminalResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Set a carrier's terminal as the default carrier's terminal
   * @param params The `CarriersService.SetCarrierTerminalAsDefaultParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `terminalUid`: The object ID. Must be a valid ID.
   */
  SetCarrierTerminalAsDefaultResponse(params: CarriersService.SetCarrierTerminalAsDefaultParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/terminals/${encodeURIComponent(String(params.terminalUid))}/default`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Set a carrier's terminal as the default carrier's terminal
   * @param params The `CarriersService.SetCarrierTerminalAsDefaultParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `terminalUid`: The object ID. Must be a valid ID.
   */
  SetCarrierTerminalAsDefault(params: CarriersService.SetCarrierTerminalAsDefaultParams): __Observable<null> {
    return this.SetCarrierTerminalAsDefaultResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Get a carrier's drivers
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierDriversResponse(uid: string): __Observable<__StrictHttpResponse<Array<User>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/carriers/${encodeURIComponent(String(uid))}/drivers`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<User>>;
      })
    );
  }
  /**
   * Get a carrier's drivers
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierDrivers(uid: string): __Observable<Array<User>> {
    return this.getCarrierDriversResponse(uid).pipe(
      __map(_r => _r.body as Array<User>)
    );
  }

  /**
   * Link a driver to a carrier
   * @param params The `CarriersService.LinkDriverToCarrierParams` containing the following parameters:
   *
   * - `userUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  linkDriverToCarrierResponse(params: CarriersService.LinkDriverToCarrierParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/drivers/${encodeURIComponent(String(params.userUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Link a driver to a carrier
   * @param params The `CarriersService.LinkDriverToCarrierParams` containing the following parameters:
   *
   * - `userUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  linkDriverToCarrier(params: CarriersService.LinkDriverToCarrierParams): __Observable<null> {
    return this.linkDriverToCarrierResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Unlink a carrier's driver
   * @param params The `CarriersService.UnlinkCarrierDriverParams` containing the following parameters:
   *
   * - `userUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  unlinkCarrierDriverResponse(params: CarriersService.UnlinkCarrierDriverParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/drivers/${encodeURIComponent(String(params.userUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Unlink a carrier's driver
   * @param params The `CarriersService.UnlinkCarrierDriverParams` containing the following parameters:
   *
   * - `userUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  unlinkCarrierDriver(params: CarriersService.UnlinkCarrierDriverParams): __Observable<null> {
    return this.unlinkCarrierDriverResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Get a carrier's operators
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierOperatorsResponse(uid: string): __Observable<__StrictHttpResponse<Array<User>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/carriers/${encodeURIComponent(String(uid))}/operators`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<User>>;
      })
    );
  }
  /**
   * Get a carrier's operators
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierOperators(uid: string): __Observable<Array<User>> {
    return this.getCarrierOperatorsResponse(uid).pipe(
      __map(_r => _r.body as Array<User>)
    );
  }

  /**
   * Link an operator to a carrier
   * @param params The `CarriersService.LinkOperatorToCarrierParams` containing the following parameters:
   *
   * - `userUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  linkOperatorToCarrierResponse(params: CarriersService.LinkOperatorToCarrierParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/operators/${encodeURIComponent(String(params.userUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Link an operator to a carrier
   * @param params The `CarriersService.LinkOperatorToCarrierParams` containing the following parameters:
   *
   * - `userUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  linkOperatorToCarrier(params: CarriersService.LinkOperatorToCarrierParams): __Observable<null> {
    return this.linkOperatorToCarrierResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Unlink a carrier's operator
   * @param params The `CarriersService.UnlinkCarrierOperatorParams` containing the following parameters:
   *
   * - `userUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  unlinkCarrierOperatorResponse(params: CarriersService.UnlinkCarrierOperatorParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/operators/${encodeURIComponent(String(params.userUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Unlink a carrier's operator
   * @param params The `CarriersService.UnlinkCarrierOperatorParams` containing the following parameters:
   *
   * - `userUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  unlinkCarrierOperator(params: CarriersService.UnlinkCarrierOperatorParams): __Observable<null> {
    return this.unlinkCarrierOperatorResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Get a carrier's vehicles
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierVehiclesResponse(uid: string): __Observable<__StrictHttpResponse<Array<Vehicle>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/carriers/${encodeURIComponent(String(uid))}/vehicles`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<Vehicle>>;
      })
    );
  }
  /**
   * Get a carrier's vehicles
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierVehicles(uid: string): __Observable<Array<Vehicle>> {
    return this.getCarrierVehiclesResponse(uid).pipe(
      __map(_r => _r.body as Array<Vehicle>)
    );
  }

  /**
   * Create a Vehicle
   * @param params The `CarriersService.CreateCarrierVehicleParams` containing the following parameters:
   *
   * - `vehiclecreate`: The vehicle create object
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  createCarrierVehicleResponse(params: CarriersService.CreateCarrierVehicleParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.vehiclecreate;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/vehicles`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Create a Vehicle
   * @param params The `CarriersService.CreateCarrierVehicleParams` containing the following parameters:
   *
   * - `vehiclecreate`: The vehicle create object
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  createCarrierVehicle(params: CarriersService.CreateCarrierVehicleParams): __Observable<null> {
    return this.createCarrierVehicleResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Get a carrier's vehicle
   * @param params The `CarriersService.GetCarrierVehicleParams` containing the following parameters:
   *
   * - `vehicleUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getCarrierVehicleResponse(params: CarriersService.GetCarrierVehicleParams): __Observable<__StrictHttpResponse<Vehicle>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/vehicles/${encodeURIComponent(String(params.vehicleUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Vehicle>;
      })
    );
  }
  /**
   * Get a carrier's vehicle
   * @param params The `CarriersService.GetCarrierVehicleParams` containing the following parameters:
   *
   * - `vehicleUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getCarrierVehicle(params: CarriersService.GetCarrierVehicleParams): __Observable<Vehicle> {
    return this.getCarrierVehicleResponse(params).pipe(
      __map(_r => _r.body as Vehicle)
    );
  }

  /**
   * Update a Carrier Vehicle
   * @param params The `CarriersService.UpdateCarrierVehicleParams` containing the following parameters:
   *
   * - `vehicleUpdate`: The vehicle update object
   *
   * - `vehicleUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  updateCarrierVehicleResponse(params: CarriersService.UpdateCarrierVehicleParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.vehicleUpdate;


    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/vehicles/${encodeURIComponent(String(params.vehicleUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Update a Carrier Vehicle
   * @param params The `CarriersService.UpdateCarrierVehicleParams` containing the following parameters:
   *
   * - `vehicleUpdate`: The vehicle update object
   *
   * - `vehicleUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  updateCarrierVehicle(params: CarriersService.UpdateCarrierVehicleParams): __Observable<null> {
    return this.updateCarrierVehicleResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Delete a Carrier Vehicle
   * @param params The `CarriersService.DeleteCarrierVehicleParams` containing the following parameters:
   *
   * - `vehicleUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  deleteCarrierVehicleResponse(params: CarriersService.DeleteCarrierVehicleParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/vehicles/${encodeURIComponent(String(params.vehicleUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete a Carrier Vehicle
   * @param params The `CarriersService.DeleteCarrierVehicleParams` containing the following parameters:
   *
   * - `vehicleUid`: The object ID. Must be a valid ID.
   *
   * - `uid`: The object ID. Must be a valid ID.
   */
  deleteCarrierVehicle(params: CarriersService.DeleteCarrierVehicleParams): __Observable<null> {
    return this.deleteCarrierVehicleResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Get a carrier's contracts
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierContractsResponse(uid: string): __Observable<__StrictHttpResponse<Array<MainCarrierContract>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/carriers/${encodeURIComponent(String(uid))}/contracts`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<MainCarrierContract>>;
      })
    );
  }
  /**
   * Get a carrier's contracts
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getCarrierContracts(uid: string): __Observable<Array<MainCarrierContract>> {
    return this.getCarrierContractsResponse(uid).pipe(
      __map(_r => _r.body as Array<MainCarrierContract>)
    );
  }

  /**
   * Create a carrier contract
   * @param params The `CarriersService.CreateCarrierContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `carriercontractcreate`: The carrier contract create object
   */
  createCarrierContractResponse(params: CarriersService.CreateCarrierContractParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.carriercontractcreate;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/contracts`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Create a carrier contract
   * @param params The `CarriersService.CreateCarrierContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `carriercontractcreate`: The carrier contract create object
   */
  createCarrierContract(params: CarriersService.CreateCarrierContractParams): __Observable<null> {
    return this.createCarrierContractResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Retrieve a carrier Contract
   * @param params The `CarriersService.RetrieveCarrierContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `carrierContractUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  retrieveCarrierContractResponse(params: CarriersService.RetrieveCarrierContractParams): __Observable<__StrictHttpResponse<CarrierContract>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/contracts/${encodeURIComponent(String(params.carrierContractUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<CarrierContract>;
      })
    );
  }
  /**
   * Retrieve a carrier Contract
   * @param params The `CarriersService.RetrieveCarrierContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `carrierContractUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  retrieveCarrierContract(params: CarriersService.RetrieveCarrierContractParams): __Observable<CarrierContract> {
    return this.retrieveCarrierContractResponse(params).pipe(
      __map(_r => _r.body as CarrierContract)
    );
  }

  /**
   * Update a Carrier Contract
   * @param params The `CarriersService.UpdateCarrierContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `carriercontractUpdate`: The carrier contract update object
   *
   * - `carrierContractUid`: The object ID. Must be a valid ID.
   */
  updateCarrierContractResponse(params: CarriersService.UpdateCarrierContractParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.carriercontractUpdate;

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/contracts/${encodeURIComponent(String(params.carrierContractUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Update a Carrier Contract
   * @param params The `CarriersService.UpdateCarrierContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `carriercontractUpdate`: The carrier contract update object
   *
   * - `carrierContractUid`: The object ID. Must be a valid ID.
   */
  updateCarrierContract(params: CarriersService.UpdateCarrierContractParams): __Observable<null> {
    return this.updateCarrierContractResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Delete a carrier contract
   * @param params The `CarriersService.DeleteCarrierContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `carrierContractUid`: The object ID. Must be a valid ID.
   */
  deleteCarrierContractResponse(params: CarriersService.DeleteCarrierContractParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/carriers/${encodeURIComponent(String(params.uid))}/contracts/${encodeURIComponent(String(params.carrierContractUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete a carrier contract
   * @param params The `CarriersService.DeleteCarrierContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `carrierContractUid`: The object ID. Must be a valid ID.
   */
  deleteCarrierContract(params: CarriersService.DeleteCarrierContractParams): __Observable<null> {
    return this.deleteCarrierContractResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }
}

namespace CarriersService {

  /**
   * Parameters for searchCarriers
   */
  export interface SearchCarriersParams {

    /**
     * Indicates the field to use for sorting the data:
     *   * NAME : the carrier's name
     */
    sortFieldName?: 'NAME';

    /**
     * Sort direction : ASC, DESC
     */
    sortDirection?: 'ASC' | 'DESC';

    /**
     * The carrier search options object
     */
    search?: CarrierSearchOptions;

    /**
     * The list items offset
     */
    offset?: number;

    /**
     * The list items limit
     */
    limit?: number;
  }

  /**
   * Parameters for linkTerminalToCarrier
   */
  export interface LinkTerminalToCarrierParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    terminalUid: string;
  }

  /**
   * Parameters for unlinkCarrierTerminal
   */
  export interface UnlinkCarrierTerminalParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    terminalUid: string;
  }

  /**
   * Parameters for SetCarrierTerminalAsDefault
   */
  export interface SetCarrierTerminalAsDefaultParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    terminalUid: string;
  }

  /**
   * Parameters for linkDriverToCarrier
   */
  export interface LinkDriverToCarrierParams {

    /**
     * The object ID. Must be a valid ID.
     */
    userUid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;
  }

  /**
   * Parameters for unlinkCarrierDriver
   */
  export interface UnlinkCarrierDriverParams {

    /**
     * The object ID. Must be a valid ID.
     */
    userUid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;
  }

  /**
   * Parameters for linkOperatorToCarrier
   */
  export interface LinkOperatorToCarrierParams {

    /**
     * The object ID. Must be a valid ID.
     */
    userUid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;
  }

  /**
   * Parameters for unlinkCarrierOperator
   */
  export interface UnlinkCarrierOperatorParams {

    /**
     * The object ID. Must be a valid ID.
     */
    userUid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;
  }

  /**
   * Parameters for createCarrierVehicle
   */
  export interface CreateCarrierVehicleParams {

    /**
     * The vehicle create object
     */
    vehiclecreate: VehicleCreate;

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;
  }

  /**
   * Parameters for getCarrierVehicle
   */
  export interface GetCarrierVehicleParams {

    /**
     * The object ID. Must be a valid ID.
     */
    vehicleUid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;
  }

  /**
   * Parameters for updateCarrierVehicle
   */
  export interface UpdateCarrierVehicleParams {

    /**
     * The vehicle update object
     */
    vehicleUpdate: VehicleUpdate;

    /**
     * The object ID. Must be a valid ID.
     */
    vehicleUid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;
  }

  /**
   * Parameters for deleteCarrierVehicle
   */
  export interface DeleteCarrierVehicleParams {

    /**
     * The object ID. Must be a valid ID.
     */
    vehicleUid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;
  }

  /**
   * Parameters for createCarrierContract
   */
  export interface CreateCarrierContractParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The carrier contract create object
     */
    carriercontractcreate: CarrierContractCreate;
  }

  /**
   * Parameters for retrieveCarrierContract
   */
  export interface RetrieveCarrierContractParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    carrierContractUid: string;
  }

  /**
   * Parameters for updateCarrierContract
   */
  export interface UpdateCarrierContractParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The carrier contract update object
     */
    carriercontractUpdate: CarrierContractUpdate;

    /**
     * The object ID. Must be a valid ID.
     */
    carrierContractUid: string;
  }

  /**
   * Parameters for deleteCarrierContract
   */
  export interface DeleteCarrierContractParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    carrierContractUid: string;
  }
}

export { CarriersService }
